// Humanize timing on a voice — adds a subtle .late(rand-like-source)
// wobble so successive cycles don't sound mechanical.
//
// Two approaches considered:
//   1. Per-event jitter encoded as a long mini-notation string of
//      offsets. Faithful to the seed but bloats the source.
//   2. A `rand.range(-jitter, jitter)` modulator arg on .late.
//      Strudel's `rand` is its own seeded signal — the harness can't
//      reproduce a specific cycle's offsets, but the overall character
//      survives round-trip and the source stays tidy.
//
// We pick #2 — the agent and user both reach for "humanize" as a feel
// thing, not a forensic-replay thing.

import { newBlockId, type Numeric, type SimpleEffect } from '../blocks/types';

export type HumanizeOptions = {
  /** Maximum jitter in cycles (0.005 ≈ 12ms at 124 cpm). Default 0.005. */
  jitter?: number;
};

export function buildHumanizeEffect(options: HumanizeOptions = {}): SimpleEffect {
  const j = clampJitter(options.jitter ?? 0.005);
  const rand: Numeric = {
    kind: 'modulator',
    source: 'rand',
    range: [
      { kind: 'num', value: -j },
      { kind: 'num', value: j },
    ],
  };
  return {
    id: newBlockId(),
    kind: 'late',
    args: [{ kind: 'numeric', value: rand }],
  };
}

function clampJitter(j: number): number {
  if (!Number.isFinite(j) || j <= 0) return 0.005;
  // Above ~0.05 (≈ 1/20 of a cycle) it stops sounding like humanisation
  // and starts sounding like a slop effect — cap there.
  return Math.min(j, 0.05);
}
